import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Snackbar, Alert, Chip, Stack } from "@mui/material";

import ProgressStepper from "./IntroductionStepper";
import IntroductionStartCard from "./IntroductionStartCard";
import IntroductionPagination from "./IntroductionPagination";
import IntroductionDialog from "./IntroductionDialog";
import ExperimentForm from "../ExperimentForm";
import Timeline from "../Timeline";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

export default function Introduction({
  showConfirmDialog,
  onConfirmDialogClose,
  forceReset,
  onForceResetHandled,
  onSaveProject,
  projectToEdit,
}) {
  const [step, setStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [pageCount, setPageCount] = useState("");
  const [formData, setFormData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    if (projectToEdit) {
      setProjectName(projectToEdit.name);
      setFormData(projectToEdit.data || []);
      setStep(1);
    }
  }, [projectToEdit]);

  useEffect(() => {
    if (showConfirmDialog && step > 0) {
      setOpenDialog(true);
    } else {
      onConfirmDialogClose(false);
    }
  }, [showConfirmDialog, step, finished, onConfirmDialogClose]);

  useEffect(() => {
    if (forceReset) {
      resetForm();
      onForceResetHandled();
    }
  }, [forceReset, onForceResetHandled]);

  const handleStart = useCallback(() => { // useCallback pour que handleStart reste la même tant que certaines valeurs ne changent pas
    const count = parseInt(pageCount);
    if (!isNaN(count) && count > 0) {
      const initialData = Array.from({ length: count }, () => ({
        interventions: [{ lastIntervention: "yes" }],
      }));
      setFormData(initialData);
      setStep(1);
    }
  }, [pageCount]);

// enter bouton
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === "Enter" &&
        step === 0 &&
        pageCount.trim() !== "" &&
        !isNaN(pageCount) &&
        parseInt(pageCount) > 0 &&
        projectName.trim() !== ""
      ) {
        handleStart();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [step, pageCount, projectName, handleStart]);
  
  const resetForm = () => {
    setStep(0);
    setFinished(false);
    setPageCount("");
    setFormData([]);
    setProjectName("");
  };

  const submit = () => {
    const experimentDuration = parseInt(formData[0]?.experimentDuration || "0", 10);
    let maxDay = 0;
  
    formData.forEach((entry) => {
      const acclimatation = parseInt(entry.acclimatationDays || 0, 10);
      if (acclimatation > 0) maxDay = Math.max(maxDay, acclimatation + 1);
  
      const preparation = parseInt(entry.preparationDays || 0, 10);
      if (preparation > 0) maxDay = Math.max(maxDay, preparation + 1);
  
      entry.interventions.forEach((intervention) => {
        const start = parseInt(intervention.daysStart || 0, 10);
  
        if (intervention.repeatedIntervention === "every_day" && intervention.xDays) {
          for (let i = 0; i < parseInt(intervention.xDays, 10); i++) {
            maxDay = Math.max(maxDay, start + i);
          }
        } else if (
          intervention.repeatedIntervention === "every_x_days" &&
          intervention.xDays &&
          intervention.yTimes
        ) {
          for (let i = 0; i < parseInt(intervention.yTimes, 10); i++) {
            maxDay = Math.max(maxDay, start + i * parseInt(intervention.xDays, 10));
          }
        } else if (intervention.repeatedIntervention === "after_x_days" && intervention.xDays) {
          maxDay = Math.max(maxDay, start + parseInt(intervention.xDays, 10));
        } else {
          maxDay = Math.max(maxDay, start);
        }
      });
    });
  
    if (maxDay > experimentDuration) {
      alert(
        `⛔ The timeline exceeds the declared experiment duration (${experimentDuration} days).\nLast event is on day ${maxDay}. Please correct this before continuing.`
      );
      return;
    }
    onSaveProject(projectName, formData);
    setShowSnackbar(true);
    setFinished(true);

  };

  const handleConfirm = (confirm) => {
    if (confirm) {
      resetForm();
    }
    setOpenDialog(false);
    onConfirmDialogClose(confirm);
  };

  if (step === 0) {
    return (
      <IntroductionStartCard
        pageCount={pageCount}
        setPageCount={setPageCount}
        handleStart={handleStart}
        projectName={projectName}
        setProjectName={setProjectName}
      />
    );
  }

  // génération timeline
  if (finished) {
    return (
      <Box sx={{ p: 6, pt: 10 }}>
        <ProgressStepper activeStep={1} />
        <Typography variant="h5" mb={4} textAlign="center">
          Experiment Timeline
        </Typography>

        {formData.map((expData, i) => (
          <Box key={i} mb={6}>
            <Typography variant="h6" gutterBottom>
              Timeline for Experiment {i + 1}
            </Typography>
            <Timeline data={[expData]} index={i} projectName={projectName} />
          </Box>
        ))}


        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setShowSnackbar(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            Project saved successfully!
          </Alert>
        </Snackbar>
        <IntroductionDialog open={openDialog} handleConfirm={handleConfirm} finished={true} />
      </Box>
    );
  }

  // le formulaire 
  return (
    <Box className="container">
      <header>
        <ProgressStepper activeStep={0} />
        <Box mt={3}>
          <Typography variant="h5" mb={2} textAlign="center">
            Experiment Form {step}
          </Typography>
          <ExperimentForm
            index={step - 1}
            data={formData}
            setData={setFormData}
          />
        </Box>
        <IntroductionPagination
          step={step}
          setStep={setStep}
          total={formData.length}
          submit={submit}
        />
      <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
        <Chip
          label="Add Page"
          icon={<AddCircleOutlineIcon />}
          variant="outlined"
          onClick={() => {
            const newFormData = [...formData];
            newFormData.splice(step, 0, {
              interventions: [{ lastIntervention: "yes" }],
            });
            setFormData(newFormData);
          }}
          sx={{
            borderColor: "primary.main",
            color: "primary.main",
            "&:hover": { backgroundColor: "primary.light", color: "white" },
          }}
        />

          <Chip
            label="Delete Page"
            icon={<DeleteOutlineIcon />}
            variant="outlined"
            disabled={formData.length <= 1}
            onClick={() => {
              if (formData.length > 1) {
                const newFormData = [...formData];
                newFormData.splice(step - 1, 1);
                setFormData(newFormData);
                setStep((prev) => Math.max(1, prev - 1));
              }
            }}
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                backgroundColor:
                  formData.length > 1 ? "error.light" : "inherit",
                color: formData.length > 1 ? "white" : "inherit",
              },
            }}
          />
        </Stack>
      </header>

      <IntroductionDialog open={openDialog} handleConfirm={handleConfirm} finished={false} />
    </Box>
  );
}
